import { appendAIPaneFromCurrentUrl } from "../../utils/aiPaneUrl";

export const handleGoBackToFillForm = (setConfirmCloseModal, setShowFillModal) => {
    setConfirmCloseModal(false)
    setShowFillModal(true)
  }

export const handleCancelAction = (setShowFillModal, setConfirmCloseModal, setFile, setShowUploadedFile) => {
    setShowFillModal(false)
    setConfirmCloseModal(false)
    setFile(null)
    setShowUploadedFile(false);
  }

export const handleShowConfirmModal = (setConfirmCloseModal) => {
    setConfirmCloseModal(true)
  }

export const getFileType = (file) => {
    const ext = file?.name?.split('.').pop().toLowerCase();
    const validTypes = ['pdf', 'docx', 'xlsx'];
    if (validTypes.includes(ext)) {
      return ext;
    }

    // fallback to mime
    const mimeMap = {
      'application/pdf': 'pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    };

    return mimeMap[file?.type] || '';
  };

export const getFileTypeFromResponse = (responseType) => {
    if (!responseType) return null;

    if (responseType === 'application/pdf') {
      return "pdf";
    }

    const typeParts = responseType.split('/'); // ["application", "vnd.openxmlformats-officedocument.wordprocessingml.document"]
    const subtype = typeParts[1] || "";
    console.log(subtype);
    if (subtype.includes("wordprocessingml.document")) {
      return "docx";
    }

    if (subtype.includes("spreadsheetml.sheet")) {
      return "xlsx";
    }
  }

export const handleFormFillModalClose = (setShowFillModal, setShowTemplatePopup) => {
    setShowFillModal(false);
    setShowTemplatePopup(true);
  }

/**
 * Build the `results` map sent to Formstack for both template merge and
 * preview requests.
 *
 * Formstack silently returns HTTP 200 + application/json error body
 * (instead of a PDF) when a template references merge tags that are
 * omitted from the payload entirely — see dms_backend PR #113 and
 * Recallium #1040. Always emit a string for every form-value key (`""`
 * for null/undefined), trim whitespace on string inputs, and pass
 * non-string values through untouched.
 *
 * Shared between `handlePreviewMergingTemplate` and `mergeTemplate` so
 * the preview and generate paths can never silently diverge if the
 * hardening logic ever needs to change (Bugbot #66, Low).
 */
export const buildFormstackPayloadResult = (formValues) => {
  if (!Array.isArray(formValues)) return {};
  return formValues.reduce((acc, item) => {
    if (!item?.key) return acc;
    const raw = item.value;
    if (raw === undefined || raw === null) {
      acc[item.key] = "";
    } else if (typeof raw === "string") {
      acc[item.key] = raw.trim();
    } else {
      acc[item.key] = raw;
    }
    return acc;
  }, {});
};

export const handleGeneratedSuccessClose = ({setShowGeneratedSuccess, dispatch, setTemplateFields, setMergedDocument, setFormValues, setSelectedTemplate, setShowUploadedFile, setShowGeneratedLoader, setFile, setSelectedLetterhead}) => {
    setShowGeneratedSuccess(false)
    dispatch(setTemplateFields({}))
    dispatch(setMergedDocument({}))
    setFormValues([])
    setSelectedTemplate(null)
    setShowUploadedFile(false)
    setShowGeneratedLoader(false)
    setFile(null)
    setSelectedLetterhead?.(null)
  }

  export const handlePreviewMergingTemplate = async ({formValues, selectedTemplate, selectedLetterhead, setTemplatePreview, setOpenPreview, setErrorToastMsgAndShow, setShowFillModal, setShowGeneratedLoader, setShowGeneratedSuccess, dispatch, previewTemplateFields, getSelectedKey, findFolderNode, treeData, templateFields, matterDetails}) => {
    try {

      const result = buildFormstackPayloadResult(formValues);

      const selectedKey = getSelectedKey();
      const currentFolder = selectedKey ? findFolderNode(treeData, selectedKey) : null;

      const payload = {
        templateId: templateFields.document_id,
        results: result,
        query: {
          route_key: selectedTemplate.key,
          output_type: selectedTemplate.output,
          // Post split-schema, matter serializers no longer emit `sf_id`;
          // `id` IS the Salesforce text id the backend resolves.
          matter: matterDetails.id || matterDetails.sf_id || matterDetails.sf_matter_id
        }
      };
      if (currentFolder?.id) {
        payload.query.folder = currentFolder.id;
      }
      // Add letterhead_id to query params if letterhead is selected
      if (selectedLetterhead?.id) {
        payload.query.letterhead_id = selectedLetterhead.id;
      }

      const response = await dispatch(previewTemplateFields(payload)).unwrap();
 
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        // Get content type from blob
        const contentType = response.data.type || "application/pdf";
        const fileType = getFileTypeFromResponse(contentType) || "pdf";
        
        const responseData = {
          file_content: base64String,
          file_type: fileType,
          content_type: contentType,
          filename: selectedTemplate.name
        };
        setTemplatePreview(responseData);
        setOpenPreview(true);
      };
      reader.onerror = () => {
        console.error('Error reading preview blob');
        setErrorToastMsgAndShow("Failed to process preview content.");
        setShowFillModal(true);
        setShowGeneratedLoader(false);
      };
      reader.readAsDataURL(response.data);
  
    } catch (err) {
      console.error('Template preview failed:', err);
      setErrorToastMsgAndShow(
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to preview template."
      );
      setShowFillModal(true);
      setShowGeneratedLoader(false);
      setShowGeneratedSuccess(false);
    }
  };

  // Apryse-first template merge flow (Phase 1).
  //
  // For library templates whose `output` is "pdf", skip FillFormFieldsModal entirely:
  // Salesforce auto-populates field values on fetchTemplateFields; Formstack is configured
  // to return unflattened PDFs; Apryse renders AcroForm widgets as editable. Paralegals
  // land directly on the editable merged PDF in the Matter tab, instead of typing values
  // into a context-free form that produces the same output. DOCX/XLSX templates keep the
  // form-modal flow because they do not land in Apryse and have no editable-widget affordance.
  //
  // Uploaded files (drag-and-drop template upload from local disk) always pass through the
  // form modal — we can't know ahead of time that the file has SF-ready mappings.
  export const onTemplateNext = async ({
    file,
    setShowTemplatePopup,
    setUploadProgress,
    setShowUploadProgress,
    setSelectedTemplate,
    setFormValues,
    setShowFillModal,
    dispatch,
    fetchTemplateFields,
    animateProgress,
    handleError,
    setIsUploadLoading,
    uploadTemplate,
    selectedTemplate,
    selectedLetterhead,
    setErrorToastMsgAndShow,
    matterDetails,
    // Direct-merge dependencies (library PDF path only)
    setShowGeneratedLoader,
    setShowGeneratedSuccess,
    fetchMatterDetail,
    fetchMatterDetailList,
    fetchMatterDetailsFolder,
    mergeTemplateFields,
    getSelectedKey,
    treeData,
    id,
    navigate,
    postHighlightedMatter,
    // Send Doc for E-Signature: hand the generated document on instead of
    // opening it or showing the success modal.
    onGenerated = null,
  }) => {
    if (file) {
      const formData = new FormData();
      formData.append('name', file?.name);
      formData.append('type', getFileType(file));
      formData.append('output', getFileType(file));
      formData.append('file', file);
      formData.append('output_name', file.name);

      setShowTemplatePopup(false);
      setUploadProgress(10);
      setShowUploadProgress(true);

      try {
        animateProgress(10, 50, 1000, setUploadProgress);
        const uploadAPI = await dispatch(uploadTemplate(formData)).unwrap();

        animateProgress(50, 100, 800, setUploadProgress);
        const allTempFields = await dispatch(fetchTemplateFields(uploadAPI?.data?.id)).unwrap();

        setSelectedTemplate(uploadAPI?.data);
        setFormValues(allTempFields?.data?.fields);

        setTimeout(() => {
          setShowUploadProgress(false);
          setUploadProgress(0);
          setShowFillModal(true);
        }, 600);

      } catch (err) {
        console.error("onTemplateNext FILE error:", err);
        handleError(err?.response?.data?.error || err?.response?.data?.detail || "Failed to Upload file");
        setShowUploadProgress(false);
        setShowTemplatePopup(true);
        setUploadProgress(0);
      }

    } else {
      try {
        setIsUploadLoading(true);

        const isPdfTemplate = selectedTemplate?.output === 'pdf';
        const canDirectMerge = isPdfTemplate
          && typeof setShowGeneratedLoader === 'function'
          && typeof mergeTemplateFields === 'function'
          && typeof navigate === 'function';

        if (canDirectMerge) {
          // One click, one request. The server fills every field from the
          // matter's Salesforce record during the merge (dms_backend
          // #2426), so there is nothing to fetch and nothing to send back;
          // the merge response carries the stamped values for the viewer's
          // empty-field highlighting and edit audit.
          setFormValues([]);
          setShowTemplatePopup(false);
          setIsUploadLoading(false);
          await mergeTemplate({
            formValues: [],
            selectedTemplate,
            selectedLetterhead,
            // No FillFormFieldsModal in the direct-merge path — recovery goes
            // via setShowTemplatePopup below so the user lands back on the
            // template-selection popup instead of a dead toast (Bugbot #66
            // finding 53469bab).
            setShowFillModal: () => {},
            setShowTemplatePopup,
            setShowGeneratedLoader,
            setShowGeneratedSuccess,
            setErrorToastMsgAndShow,
            dispatch,
            fetchMatterDetail,
            fetchMatterDetailList,
            fetchMatterDetailsFolder,
            mergeTemplateFields,
            getSelectedKey,
            findFolderNode,
            treeData,
            templateFields: { document_id: selectedTemplate.id },
            matterDetails,
            id,
            autoOpen: true,
            navigate,
            postHighlightedMatter,
            onGenerated,
          });
        } else {
          // Word/Excel templates keep the fill-in form: the fetched fields
          // are what the form displays. Whatever the form sends back is
          // laid over the server's own fill, so a typed value still wins.
          const allTempFields = await dispatch(
            fetchTemplateFields({ templateId: selectedTemplate.id, matter_id: matterDetails.id })
          ).unwrap();
          setFormValues(allTempFields?.data?.fields || []);
          setShowTemplatePopup(false);
          setIsUploadLoading(false);
          setShowFillModal(true);
        }
      } catch (err) {
        console.error("onTemplateNext TEMPLATE error:", err);
        setErrorToastMsgAndShow(
          err?.response?.data?.error || err?.response?.data?.detail || "Failed to fetch template fields."
        );
        setShowTemplatePopup(true);
        setFormValues([]);
        setIsUploadLoading(false);
      }
    }
  };

  export const mergeTemplate = async ({
    formValues,
    selectedTemplate,
    selectedLetterhead,
    setShowFillModal,
    setShowTemplatePopup,
    setShowGeneratedLoader,
    setShowGeneratedSuccess,
    setErrorToastMsgAndShow,
    dispatch,
    fetchMatterDetail,
    fetchMatterDetailList,
    fetchMatterDetailsFolder,
    mergeTemplateFields,
    getSelectedKey,
    findFolderNode: findFolderNodeFn = findFolderNode,
    treeData,
    templateFields,
    matterDetails,
    id,
    autoOpen = false,
    navigate,
    postHighlightedMatter,
    poaAttorney = "",
    onGenerated = null,
  }) => {
    // Recovery routing — in the direct-merge/autoOpen path there is no
    // FillFormFieldsModal to return to, so failures must reopen the template
    // selection popup or the user gets stranded on a toast (Bugbot #66,
    // 53469bab).
    const recoverFromMergeFailure = () => {
      if (autoOpen && typeof setShowTemplatePopup === 'function') {
        setShowTemplatePopup(true);
      } else {
        setShowFillModal(true);
      }
    };

    try {
      if (!selectedTemplate) {
        console.error('mergeTemplate: selectedTemplate is null');
        setErrorToastMsgAndShow("Template information is missing. Please go back and select a template again.");
        recoverFromMergeFailure();
        setShowGeneratedLoader(false);
        return;
      }

      if (!selectedTemplate.key || !selectedTemplate.output) {
        console.error('mergeTemplate: selectedTemplate is missing key or output', selectedTemplate);
        setErrorToastMsgAndShow("Template information is incomplete. Please go back and select a template again.");
        recoverFromMergeFailure();
        setShowGeneratedLoader(false);
        return;
      }

      if (!templateFields?.document_id) {
        console.error('mergeTemplate: templateFields.document_id is missing', templateFields);
        setErrorToastMsgAndShow("Template document ID is missing. Please go back and try again.");
        recoverFromMergeFailure();
        setShowGeneratedLoader(false);
        return;
      }

      setShowFillModal(false);
      setShowGeneratedLoader(true);

      const result = buildFormstackPayloadResult(formValues);

      const selectedKey = getSelectedKey();
      const currentFolder = selectedKey ? findFolderNodeFn(treeData, selectedKey) : null;

      const payload = {
        templateId: templateFields.document_id,
        results: result,
        query: {
          route_key: selectedTemplate.key,
          output_type: selectedTemplate.output,
          // Post split-schema, matter serializers no longer emit `sf_id`;
          // `id` IS the Salesforce text id the backend resolves.
          matter: matterDetails.id || matterDetails.sf_id || matterDetails.sf_matter_id
        }
      };
      if (currentFolder?.id) {
        payload.query.folder = currentFolder.id;
      }
      if (selectedLetterhead?.id) {
        payload.query.letterhead_id = selectedLetterhead.id;
      }
      if (poaAttorney) {
        // Power-of-attorney signature: stamped at generation for attorneys,
        // otherwise the backend records a pending attorney sign-off.
        payload.query.poa_attorney = poaAttorney;
      }
      const mergeResponse = await dispatch(mergeTemplateFields(payload)).unwrap();

      if (matterDetails.sf_id) {
        dispatch(fetchMatterDetail(matterDetails.id));
      } else {
        dispatch(fetchMatterDetailsFolder(matterDetails.id));
      }
      if (selectedKey) {
        dispatch(fetchMatterDetailList({ id: id, page: 1, folder: selectedKey }));
      } else {
        dispatch(fetchMatterDetailList({ id: id, page: 1 }));
      }
      setShowGeneratedLoader(false);

      if (typeof onGenerated === 'function' && mergeResponse?.data?.document) {
        onGenerated(mergeResponse.data.document);
        return;
      }

      if (autoOpen && navigate && postHighlightedMatter && mergeResponse?.data?.document) {
        // Direct-to-Apryse path: skip the "File Generated Successfully" confirm modal
        // and open the merged PDF in the Matter tab viewer. Field values provided by
        // Salesforce are already merged into the PDF's AcroForm widgets; empties remain
        // editable for the paralegal. The server reports exactly what it stamped
        // (`field_values`); that is the baseline the viewer audits edits against.
        openFile(mergeResponse.data, navigate, dispatch, postHighlightedMatter, {
          fromTemplateMerge: true,
          mergedFieldValues: mergeResponse?.data?.field_values || result,
        });
      } else {
        setShowGeneratedSuccess(true);
      }
    } catch (err) {
      console.error('Template merge failed:', err);
      setErrorToastMsgAndShow(err?.response?.data?.error || err?.response?.data?.detail || "Failed to merge template.");
      recoverFromMergeFailure();
      setShowGeneratedLoader(false);
      setShowGeneratedSuccess(false);
    }
  };


  export const openFile = (mergedDocument, navigate, dispatch, postHighlightedMatter, extraState = {}) => {
      const matterId = mergedDocument?.document?.matter;
      const docId = mergedDocument?.document?.id;
      const docType = "document";
      const userId = Number(localStorage.getItem("user_id"));
      const highlightPayload = {
        matterId,
        docId,
        docType,
        tabAction: {
          usr_id: userId,
          document_id: String(docId),
          matter_id: String(matterId),
          action: "open"
        }
      };

      // extraState lets callers (e.g. the direct-merge template flow) pass a
      // `fromTemplateMerge: true` signal plus the merged-field snapshot so the
      // Matter viewer can highlight empty AcroForm widgets and audit user edits
      // against the Salesforce-provided baseline.
      //
      // relative_path can be empty: documents created before dms_backend
      // fix/upload-doc-identity-fields carry no DmsMeta breadcrumb, and
      // `/matter` + '' navigates to the Matters list — the user loses the
      // document they just created. Fall back to the matter-root document
      // path (`/matter/<matterId>/D<docId>`), the same shape the backend
      // writes as its matter-root fallback.
      const relativePath = mergedDocument?.document?.relative_path;
      const basePath = relativePath
        ? `/matter${relativePath}`
        : `/matter/${matterId}/D${docId}`;
      // Carry the AI panel's `?ai=` state across so opening the merged
      // document doesn't close an open panel.
      const targetPath = appendAIPaneFromCurrentUrl(basePath);
      const navOptions = { state: { document: mergedDocument?.document, ...extraState } };

      // Direct-merge (Apryse-first) is the sole feedback channel for the
      // user — the success modal is intentionally skipped. If the highlight
      // bookkeeping rejects or returns no item, legacy callers can stay
      // silent (they're insulated by the success modal), but the direct-
      // merge caller MUST still navigate or the user is stranded with no
      // confirmation that anything happened (Bugbot #66, Medium).
      const isDirectMergePath = extraState?.fromTemplateMerge === true;

      dispatch(postHighlightedMatter(highlightPayload))
        .then((response) => {
          if (response?.payload?.data?.highlighted_item) {
            navigate(targetPath, navOptions);
            return;
          }
          if (isDirectMergePath) {
            console.warn(
              '[openFile] highlight bookkeeping returned no item; navigating fallback for direct-merge path',
              { docId, matterId },
            );
            navigate(targetPath, navOptions);
          }
        })
        .catch((err) => {
          console.error('[openFile] postHighlightedMatter dispatch failed:', err);
          if (isDirectMergePath) {
            navigate(targetPath, navOptions);
          }
        });
  }

  export function findFolderNode(tree, selectedKey) {
    for (const node of tree) {
      if (node.key === selectedKey && node.type === "folder") {
        return node;
      }
      if (node?.children?.length) {
        const found = findFolderNode(node.children, selectedKey);
        if (found) return found;
      }
    }
    return null;
  }

  export function getFileNamesInFolder(folderNode) {
    if (!folderNode?.children) return [];

    return folderNode.children
      .filter(child => child.type === "document")
      .map(doc => doc.title);
  }
  
  export function getFolderNamesInFolder(folderNode) {
    if (!folderNode?.children) return [];

    return folderNode.children
      .filter(child => child.type === "folder")
      .map(doc => doc.title);
  }

  export const mergePackageFlow = async ({
    selectedPackage,
    selectedLetterhead,
    matterDetails,
    getSelectedKey,
    findFolderNodeFn,
    treeData,
    setShowTemplatePopup,
    setShowGeneratedLoader,
    setPackageMergeResult,
    setErrorToastMsgAndShow,
    dispatch,
    fetchMatterDetail,
    fetchMatterDetailList,
    fetchMatterDetailsFolder,
    id,
    mergePackageApi,
  }) => {
    try {
      if (!selectedPackage) return;
      setShowTemplatePopup(false);
      setShowGeneratedLoader(true);

      const selectedKey = getSelectedKey();
      const currentFolder = selectedKey ? findFolderNodeFn(treeData, selectedKey) : null;

      const payload = {
        matter_id: matterDetails.id,
      };
      if (currentFolder?.id) {
        payload.folder_id = currentFolder.id;
      }
      if (selectedLetterhead?.id) {
        payload.letterhead_id = selectedLetterhead.id;
      }

      const res = await mergePackageApi(selectedPackage.id, payload);
      const result = res?.data || res;

      if (matterDetails.sf_id) {
        dispatch(fetchMatterDetail(matterDetails.id));
      } else {
        dispatch(fetchMatterDetailsFolder(matterDetails.id));
      }
      if (selectedKey) {
        dispatch(fetchMatterDetailList({ id, page: 1, folder: selectedKey }));
      } else {
        dispatch(fetchMatterDetailList({ id, page: 1 }));
      }

      setShowGeneratedLoader(false);
      setPackageMergeResult(result);
    } catch (err) {
      console.error("Package merge failed:", err);
      setErrorToastMsgAndShow(
        err?.response?.data?.error || "Failed to merge package."
      );
      setShowGeneratedLoader(false);
    }
  };